/**
 * Universal assistant customer lookup — the AI's backend tool for finding a
 * customer record directly (RLS-scoped through the signed-in session), instead
 * of driving the customer-picker UI with clicks and vision.
 *
 * Matching contract:
 * - exactly one EXACT match (full name / email / phone) → the AI proceeds with it
 * - several matches → they're broadcast as "ghost" cards on the user's screen
 *   (GHOST_CUSTOMERS_EVENT) so the user can SEE what the AI found while it asks
 *   which one is meant.
 *
 * Found rows are cached so follow-up tools (start_booking etc.) can preselect
 * the full customer record by id without another fetch.
 */
import { supabase } from '../supabase';
import type { Customer } from '../customers';

export const GHOST_CUSTOMERS_EVENT = 'assistant:ghost-customers';
/** Fired when the user taps a ghost card (picks a customer) or the X (cancels). */
export const GHOST_CHOICE_EVENT = 'assistant:ghost-choice';

export interface GhostCustomerCard {
  id: string;
  name: string;
  email: string;
  phone: string;
}

export type GhostChoice =
  | { kind: 'selected'; customer: GhostCustomerCard }
  | { kind: 'dismissed' };

/** Tell whichever AI session is live (voice or chat) what the user chose. */
export function emitGhostChoice(choice: GhostChoice): void {
  window.dispatchEvent(new CustomEvent(GHOST_CHOICE_EVENT, { detail: choice }));
}

const LOOKUP_CAP = 10;

/** Full rows from recent lookups, so start_booking can preselect without refetching. */
const customerCache = new Map<string, Customer>();

export function getCachedCustomer(id: string): Customer | null {
  return customerCache.get(id) || null;
}

function fullName(c: Customer): string {
  return `${c.first_name || ''} ${c.last_name || ''}`.trim();
}

function toCard(c: Customer): GhostCustomerCard {
  return {
    id: String(c.id),
    name: fullName(c),
    email: c.email || '',
    phone: c.phone || '',
  };
}

/** Show the candidate customers on the user's screen as ghost cards. */
export function showGhostCustomers(customers: GhostCustomerCard[], title?: string): void {
  window.dispatchEvent(
    new CustomEvent(GHOST_CUSTOMERS_EVENT, { detail: { customers, title } }),
  );
}

export function clearGhostCustomers(): void {
  window.dispatchEvent(new CustomEvent(GHOST_CUSTOMERS_EVENT, { detail: { customers: [] } }));
}

export interface CustomerLookupResult {
  exact: GhostCustomerCard[];
  similar: GhostCustomerCard[];
}

async function searchRows(businessId: string, orParts: string[]): Promise<Customer[]> {
  const { data, error } = await supabase
    .from('customers')
    .select('*')
    .eq('business_id', businessId)
    .eq('status', 'active')
    .or(orParts.join(','))
    .limit(LOOKUP_CAP * 2);
  if (error) throw error;
  return (data || []) as Customer[];
}

/**
 * Search active customers by name/email/phone, resilient to the partial and
 * slightly-off spellings voice transcription produces:
 * 1. full term (multi-word terms also match first + last name together)
 * 2. no hits → each name token separately
 * 3. still none → shortened prefixes of the first token ("harmanvir" →
 *    "harma" still finds Harmanveer)
 */
export async function lookupCustomers(businessId: string, rawQuery: string): Promise<CustomerLookupResult> {
  const term = rawQuery.replace(/[%,()]/g, ' ').trim();
  if (!term) return { exact: [], similar: [] };
  const tokens = term.split(/\s+/).filter(Boolean);

  // Pass 1: the whole term
  const like = `%${term}%`;
  const orParts = [
    `first_name.ilike.${like}`,
    `last_name.ilike.${like}`,
    `email.ilike.${like}`,
    `phone.ilike.${like}`,
  ];
  if (tokens.length >= 2) {
    orParts.push(
      `and(first_name.ilike.%${tokens[0]}%,last_name.ilike.%${tokens.slice(1).join(' ')}%)`,
    );
  }
  let rows = await searchRows(businessId, orParts);

  // Pass 2: individual tokens against first/last name
  if (rows.length === 0 && tokens.length >= 2) {
    const tokenParts = tokens
      .filter((t) => t.length >= 3)
      .flatMap((t) => [`first_name.ilike.%${t}%`, `last_name.ilike.%${t}%`]);
    if (tokenParts.length > 0) rows = await searchRows(businessId, tokenParts);
  }

  // Pass 3: shrinking prefixes of the first token (absorbs misheard endings)
  if (rows.length === 0) {
    for (const len of [5, 4, 3]) {
      if (tokens[0].length <= len) continue;
      const prefix = tokens[0].slice(0, len);
      rows = await searchRows(businessId, [`first_name.ilike.${prefix}%`, `last_name.ilike.${prefix}%`]);
      if (rows.length > 0) break;
    }
  }

  for (const c of rows) customerCache.set(String(c.id), c);

  // Exact = the query IS this customer's full name, first name, last name,
  // email, or phone. First-name-only counts: "harmanveer" said alone should
  // directly match the one customer named Harmanveer.
  const needle = term.toLowerCase();
  const needleDigits = needle.replace(/\D/g, '');
  const exact: GhostCustomerCard[] = [];
  const similar: GhostCustomerCard[] = [];
  for (const c of rows) {
    const isExact =
      fullName(c).toLowerCase() === needle ||
      (c.first_name || '').toLowerCase() === needle ||
      (c.last_name || '').toLowerCase() === needle ||
      (c.email || '').toLowerCase() === needle ||
      (needleDigits.length >= 7 && (c.phone || '').replace(/\D/g, '') === needleDigits);
    (isExact ? exact : similar).push(toCard(c));
  }
  return { exact: exact.slice(0, LOOKUP_CAP), similar: similar.slice(0, LOOKUP_CAP) };
}
